import { Localized } from "@fluent/react"
import {
    Alert,
    Box,
    Button,
    Paper,
} from "@mui/material"
import { useEffect, useState } from "react"
import { LoadingContainer } from "../../app/LoadingContainer"
import { useAppDispatch } from "../../app/hooks"
import { StructuresForm } from "./StructuresForm"
import { loadOrganizationalStructureDefinition, StructureNodeTreeDto } from "./structureSlice"
import { CreateStructureDialog } from "./dialogs/CreateStructureDialog"
import { StructureNodePanel } from "./StructureNodePanel"

export const OrganizationStructure = () => {
    const dispatch = useAppDispatch()

    const [isLoading, setIsLoading] = useState(true)
    const [structureDefinitionId, setStructureDefinitionId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [showCreateStructureButton, setShowCreateStructureButton] = useState(false)
    const [selectedNode, setSelectedNode] = useState<StructureNodeTreeDto | null>(null)
    const [createDialogOpen, setCreateDialogOpen] = useState(false)

    const fetchOrganizationalStructureDefinition = async() => {
        setIsLoading(true)
        setError(null)
        setShowCreateStructureButton(false)

        try {
            const result = await dispatch(loadOrganizationalStructureDefinition())

            if(loadOrganizationalStructureDefinition.fulfilled.match(result)) {
                setStructureDefinitionId(result.payload.id)
            } else {
                let errorMsg = "error"
                let shouldShowCreateButton = false

                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    const status = result.payload.status

                    if (problem) {
                        errorMsg = problem.title
                    }

                    shouldShowCreateButton = status === 400 || status === 404
                }

                setError(errorMsg)
                setShowCreateStructureButton(shouldShowCreateButton)
            }
        } catch {
            setError("organization-structure-load-error")
            setShowCreateStructureButton(false)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchOrganizationalStructureDefinition()
    }, [])

    if (isLoading) {
        return <LoadingContainer />
    }

    if (error) {
        return (
            <Paper sx={{ p: 2 }}>
                <Alert severity="error">
                    <Localized id={error}>{error}</Localized>
                </Alert>

                {showCreateStructureButton && (
                    <>
                        <Button
                            sx={{
                                marginTop: 2
                            }}
                            variant="contained"
                            color="primary"
                            onClick={() => setCreateDialogOpen(true)}
                        >
                            <Localized id="organization-structure-create">_Utwórz strukturę organizacyjną</Localized>
                        </Button>

                        <CreateStructureDialog
                            open={createDialogOpen}
                            onClose={() => setCreateDialogOpen(false)}
                            onSaved={() => {
                                setCreateDialogOpen(false)
                                fetchOrganizationalStructureDefinition()
                            }}
                            mode="create"
                            defaultCode="ORGANIZATIONAL_STRUCTURE"
                        />
                    </>
                )}
            </Paper>
        )
    }

    return (
        <Box
            sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.8fr) minmax(360px, 1fr)" },
                alignItems: "start",
            }}
        >
            {structureDefinitionId && (
                <StructuresForm
                    structureDefinitionId={structureDefinitionId}
                    onSelectedNodeChange={setSelectedNode}
                />
            )}

            <StructureNodePanel node={selectedNode} isRoot={!selectedNode?.parentNodeId} />
        </Box>
    )
}
